/**
 * @module utils/membershipDetails/adapters/dyntech
 * @description Único archivo del front que conoce la forma del payload de
 * Dyntech dentro de `membership.externalPlan` (typed: numeroGrupo,
 * numeroSocio; sin tipar: `raw` = última `condicionSocio`). `raw` no está
 * controlado por nosotros, así que cada campo se lee defensivamente y una
 * fila sin dato se omite.
 */

/**
 * @description ¿Es un objeto plano (no null, no array)?
 * @param {*} value
 * @returns {boolean}
 */
const isPlainObject = (value) =>
  !!value && typeof value === "object" && !Array.isArray(value);

/**
 * @description String no vacío, o `null`.
 * @param {*} value
 * @returns {string|null}
 */
const toText = (value) => {
  if (typeof value !== "string") return null;
  const text = value.trim();
  return text || null;
};

/**
 * @description Formatea una fecha ISO de Dyntech (ej. "2007-11-01T00:00:00",
 * sin zona horaria) como dd/MM/yyyy leyendo el string, sin pasar por `Date`
 * para que la zona horaria del navegador no corra el día.
 * @param {*} value
 * @returns {string|null} `null` si no es una fecha ISO válida
 */
const toDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(toText(value) || "");
  return match ? `${match[3]}/${match[2]}/${match[1]}` : null;
};

/**
 * @description Lista de códigos como texto "a, b". Acepta un string suelto
 * por si Dyntech lo manda así en vez de array.
 * @param {*} value
 * @returns {string|null}
 */
const toList = (value) => {
  const items = (Array.isArray(value) ? value : [value])
    .map(toText)
    .filter(Boolean);
  return items.length ? items.join(", ") : null;
};

/**
 * @description Detalles de credencial para un Membership de motor Dyntech.
 * @param {Object} membership - Membership vigente (con engine === 'dyntech')
 * @returns {{memberNumber: string|null, rows: Array<{key: string, label: string, value: string}>}}
 */
export default function dyntechDetails(membership) {
  const externalPlan = isPlainObject(membership.externalPlan)
    ? membership.externalPlan
    : {};
  const raw = isPlainObject(externalPlan.raw) ? externalPlan.raw : {};

  const grupo = externalPlan.numeroGrupo;
  const socio = toText(externalPlan.numeroSocio);
  const memberNumber =
    grupo !== null && grupo !== undefined && grupo !== "" && socio
      ? `${grupo}:${socio}`
      : null;

  const rows = [
    {
      key: "planAdicional",
      label: "Plan adicional",
      value: toList(raw.planAdicional),
    },
    {
      key: "condicionIva",
      label: "Condición de IVA",
      value: toText(raw.condicionIva),
    },
    { key: "vigencia", label: "Vigencia", value: toDate(raw.fechaVigencia) },
  ].filter((row) => row.value);

  return { memberNumber, rows };
}
